<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { computed } from "vue";

const modalRef = ref<HTMLElement | null>(null);

const emit = defineEmits<{
  (e: "onDismiss"): void;
  (e: "update:modelValue", value: boolean): void;
  (e: "onConfirm"): void;
}>();

const props = defineProps<{
  title?: string;
  message: string;
  modelValue: boolean;
  dismissButtonText?: string;
  confirmButtonText?: string;
  showConfirmButton?: boolean;
  showSeparators?: boolean;
  smallDismissButton?: boolean;
  styleClassName?: string;
  modalWidth?: string;
  showCloseButton?: boolean;
}>();

const modalStyle = computed(() => ({
  maxWidth: props.modalWidth || "400px",
}));

onClickOutside(modalRef, () => {
  emit("update:modelValue", false);
  emit("onDismiss");
});

const handleDismiss = () => {
  emit("update:modelValue", false);
  emit("onDismiss");
};

const handleConfirm = () => {
  emit("onConfirm");
  emit("update:modelValue", false);
};
</script>

<template>
  <div v-if="modelValue" class="confirmation-modal">
    <div
      :class="['content', styleClassName]"
      :style="modalStyle"
      ref="modalRef"
    >
      <div :class="['modal-title', { 'with-separator': showSeparators }]">
        <div class="header-content">
          <h2 v-if="title" class="title">{{ title }}</h2>
          <button
            v-if="showCloseButton"
            class="close-button"
            @click="handleDismiss"
            aria-label="Close"
          >
            <!-- Only X icon, no text, to prevent mobile layout issues -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      <div :class="['modal-body', { 'with-separator': showSeparators }]">
        <p class="message">{{ message }}</p>
      </div>
      <div :class="['modal-actions', { 'small-dismiss': smallDismissButton }]">
        <button
          :class="[
            'btn',
            showConfirmButton
              ? 'solid-outline justify-center'
              : 'solid justify-center',
            { 'w-full': !smallDismissButton, small: smallDismissButton },
          ]"
          @click="handleDismiss"
        >
          {{ dismissButtonText || "DISMISS" }}
        </button>
        <button
          v-if="showConfirmButton"
          class="btn solid w-full justify-center"
          @click="handleConfirm"
        >
          {{ confirmButtonText || "CONFIRM" }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.confirmation-modal {
  @apply fixed inset-0 z-50 flex items-center justify-center bg-black/50;

  .content {
    @apply w-full bg-white rounded-lg;

    .modal-title {
      @apply p-6 pb-3;

      &.with-separator {
        @apply border-b border-gray-200;
      }

      .header-content {
        @apply flex justify-between items-center;

        .title {
          @apply text-xl font-semibold text-left;
        }

        .close-button {
          @apply p-1 text-gray-500 hover:text-gray-700 transition-colors;
          margin: -8px;
        }
      }
    }

    .modal-body {
      @apply p-6;

      &.with-separator {
        @apply border-b border-gray-200;
      }

      .message {
        @apply text-left text-font-primary;
      }
    }

    .modal-actions {
      @apply p-6 pt-3 flex gap-4;

      &.small-dismiss {
        @apply justify-end;

        .btn.small {
          @apply w-auto px-8;
        }
      }
    }
  }
}
</style>
